/**
 * @fileoverview BasicFieldsRow Component
 *
 * Handles the basic input fields for transactions including amount, description,
 * date, user selection, and toggle switches for special options (paid directly,
 * allocate using template).
 *
 * @module components/TransactionForm/BasicFieldsRow
 */
import MoneyInput from '../common/MoneyInput';
import ToggleSwitch from '../shared/ToggleSwitch';
import styles from './BasicFieldsRow.module.css';

function BasicFieldsRow({
    transactionType,
    amount,
    description,
    date,
    currentUserKey,
    paidDirectly,
    users,
    budgetStartMonth,
    errors,
    onFieldChange,
    onCategoryChange,
    handlePositiveNumberInput: _handlePositiveNumberInput,
    clearError,
    useTemplateForIncome,
    transactionCategories,
    singleBankMode = false,
    hasTemplateForTransactionMonth = true
}) {
    // Determine if user selection should be shown
    // Hidden in single bank mode, for Adjustment transactions, or when only one user
    const showUserSelection = !singleBankMode && transactionType !== 'Adjustment' && users.length > 1;
    const handleAmountChange = (parsedValue) => {
        clearError('amount');
        const value = parsedValue !== null ? parsedValue.toString() : '';
        onFieldChange('amount', value);

        // For Transfer, auto-fill the category amount
        if (transactionType === 'Transfer' && transactionCategories.length === 1) {
            onCategoryChange('update', 0, 'amount', value);
        }

        // For Income with template, auto-allocate categories
        if (transactionType === 'Income' && useTemplateForIncome) {
            onFieldChange('templateAmount', value);
        }
    };

    const handleAmountBlur = () => {
        // No special handling needed - MoneyInput handles formatting
    };

    const handleDateChange = (newDate) => {
        onFieldChange('date', newDate);

        // Real-time validation against budget start date
        if (budgetStartMonth) {
            const budgetStart = new Date(budgetStartMonth);
            const transactionDate = new Date(newDate);

            if (transactionDate < budgetStart) {
                onFieldChange('dateError', `Transaction date cannot be before budget start date (${budgetStartMonth.split('T')[0]})`);
            } else {
                clearError('date');
            }
        } else {
            clearError('date');
        }
    };

    const getUserPromptText = () => {
        if (transactionType === 'Income') return 'Who received?';
        if (transactionType === 'Expense') return 'Who paid?';
        return 'Who initiated?';
    };

    return (
        <div className={styles.fieldsContainer}>
            {/* Amount Input */}
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Amount:</label>
                <MoneyInput
                    value={amount ? parseFloat(amount) : null}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    placeholder="0.00"
                    min={0}
                    max={9999999.99}
                    className={`${styles.amountInput} ${errors.amount ? styles.inputError : ''}`}
                />
            </div>

            {/* Description Input */}
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Description:</label>
                <input
                    type="text"
                    placeholder="Enter description"
                    value={description}
                    onChange={(e) => {
                        onFieldChange('description', e.target.value);
                        clearError('description');
                    }}
                    className={`${styles.descriptionInput} ${errors.description ? styles.inputError : ''}`}
                />
            </div>

            {/* Date Input */}
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Date:</label>
                <input
                    type="date"
                    value={date}
                    min={budgetStartMonth ? budgetStartMonth.split('T')[0] : undefined}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className={`${styles.dateInput} ${errors.date ? styles.inputError : ''}`}
                />
            </div>

            {/* User Selection - hidden in single bank mode, for Adjustments, or single-user budgets */}
            {showUserSelection && (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>{getUserPromptText()}:</label>
                    <select
                        value={currentUserKey || ''}
                        onChange={(e) => {
                            // Convert to number or null
                            const value = e.target.value ? parseInt(e.target.value, 10) : null;
                            onFieldChange('currentUserKey', value);
                            clearError('currentUserKey');
                        }}
                        className={styles.userSelect}
                    >
                        <option value="">Select user</option>
                        {users.map(user => (
                            <option key={user.userKey} value={user.userKey}>{user.userName}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Paid Directly Toggle Switch (Expenses only) - hidden in single bank mode */}
            {transactionType === 'Expense' && !singleBankMode && (
                <ToggleSwitch
                    checked={paidDirectly}
                    onChange={(checked) => onFieldChange('paidDirectly', checked)}
                    label="Paid directly from joint account"
                />
            )}

            {/* Use Template Toggle Switch (Income only) */}
            {transactionType === 'Income' && (
                <div className={styles.templateToggleContainer}>
                    <ToggleSwitch
                        checked={useTemplateForIncome}
                        onChange={(checked) => onFieldChange('useTemplateForIncome', checked)}
                        label="Allocate using budget template"
                        disabled={!hasTemplateForTransactionMonth}
                    />
                    {!hasTemplateForTransactionMonth && (
                        <span className={styles.noTemplateMessage}>
                            No budget template configured for this month
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default BasicFieldsRow;
